import React from 'react'
import { createBrowserRouter, Navigate } from 'react-router-dom'
import Layout from '@/components/Layout'
import { RootRedirect } from '@/components/auth/RootRedirect'
import { AuthGuard } from '@/components/auth/AuthGuard'

// 保持 eager（首屏关键路径 / 极小体积 / 错误兜底）：
// - LoginClerkPage：未登录用户首屏第一个看到的页面，懒加载会多一次往返
// - NotFoundPage：404 兜底，本身极小
// - TicketLoginPage：手机 App WebView 嵌入的登录入口，需要快
// - ForgotPasswordPage：登录流程内的小页面
import LoginClerkPage from '@app/routes/auth/login-clerk'
import NotFoundPage from '@app/routes/not-found'
import TicketLoginPage from '@app/routes/auth/ticket-login'
import ForgotPasswordPage from '@app/routes/auth/forgot-password'
import { PlatformAiUsageTenantRedirect } from '@/features/agentic/usage/redirects'
import { PENDING_OWNER_STAYS_PATH } from '@/features/reservation-list/ownerStayViews'
import { LEGACY_PLATFORM_AI_REDIRECTS, LEGACY_TENANT_SETTINGS_REDIRECTS, LegacyRedirect } from '@/features/agentic/shared/legacyRedirects'

// 其余所有页面都用 React.lazy 拆 chunk。
// Suspense 边界：
//   - Layout 内部已包裹 <Outlet>（src/components/Layout.tsx），覆盖所有 main 路由
//   - 顶级 auth 路由由 app/root.tsx 的 RootFallback 兜底
const RegisterClerkPage = React.lazy(() => import('@app/routes/auth/register-clerk'))
const OrganizationOnboardingPage = React.lazy(() => import('@app/routes/auth/onboarding/organization'))
const ClerkTokenDebuggerPage = React.lazy(() => import('@app/routes/debug/clerk-token'))
// MCP 附件上传落地页(公开,token 即授权)。懒加载:它只服务从聊天窗点进来的
// 一次性访客,不该进所有人的首屏 entry chunk。
const UploadLandingPage = React.lazy(() => import('@app/routes/upload/token'))
// 外部承包商免登录工单页(公开,token 即授权)。懒加载:只服务拿到链接的外部承包商。
const ContractorJobPage = React.lazy(() => import('@app/routes/job/token'))
// MCP 连接器 OAuth 回调落地页(公开,零 API 调用,只把结果交回发起页)。
const McpOAuthCallbackPage = React.lazy(() => import('@app/routes/oauth/mcp-callback'))
const IntegrationOAuthCallbackRoute = React.lazy(() => import('@app/routes/oauth/integration-callback'))

// Main 业务页面
const DashboardPage = React.lazy(() => import('@app/routes/main/dashboard'))
const AnnouncementsHistoryPage = React.lazy(() => import('@app/routes/main/announcements'))
const AdminAnnouncementsPage = React.lazy(() => import('@app/routes/main/announcements/admin'))
// Academy（内部培训学院）
const AcademyPage = React.lazy(() => import('@app/routes/main/academy'))
const AcademyAdminPage = React.lazy(() => import('@app/routes/main/academy/admin'))
const AcademyCourseDetailPage = React.lazy(() => import('@app/routes/main/academy/detail'))
const AcademyLessonPlayerPage = React.lazy(() => import('@app/routes/main/academy/player'))
const InboxPage = React.lazy(() => import('@app/routes/main/inbox'))
const IntentsPage = React.lazy(() => import('@app/routes/main/intents'))
const IntakeRequestsPage = React.lazy(() => import('@app/routes/main/intake-requests'))
const IntentDetailPage = React.lazy(() => import('@app/routes/main/intents/$id'))
const TasksPage = React.lazy(() => import('@app/routes/main/tasks'))
const SupportCasesPage = React.lazy(() => import('@app/routes/main/support-cases'))
const MyTasksPage = React.lazy(() => import('@app/routes/main/my-tasks'))
const MessagesPage = React.lazy(() => import('@app/routes/main/messages'))
const TaskDetailPage = React.lazy(() => import('@app/routes/main/tasks/detail'))
const TaskTriagePage = React.lazy(() => import('@app/routes/main/task-triage'))
const TaskCalendarRoute = React.lazy(() => import('@app/routes/main/task-calendar'))
const ResourceMapViewPage = React.lazy(() => import('@app/routes/main/resource-map-view'))
const ListingsPage = React.lazy(() => import('@app/routes/main/listing'))
const ListingsDetailPage = React.lazy(() => import('@app/routes/main/listing/detail'))
const ReservationsPage = React.lazy(() => import('@app/routes/main/reservations'))
const ReservationDetailPage = React.lazy(() => import('@app/routes/main/reservations/detail'))
const UsersPage = React.lazy(() => import('@app/routes/main/users'))
const UserDetailPage = React.lazy(() => import('@app/routes/main/users/detail'))
const TaskKindsRoute = React.lazy(() => import('@app/routes/main/task-kinds'))
// AI Agent 平台（照 AgenticOS 重建，main-api docs/superpowers/plans/2026-09-26-agenticos-frontend-plan.md）
const SettingsVaultRoute = React.lazy(() => import('@app/routes/main/settings-vault'))
const SettingsModelsRoute = React.lazy(() => import('@app/routes/main/settings-models'))
const PlatformVaultRoute = React.lazy(() => import('@app/routes/main/platform-vault'))
// Integrations（只读，main-api docs/architecture/INTEGRATION_ARCHITECTURE.md）
const SettingsIntegrationsRoute = React.lazy(() => import('@app/routes/main/settings-integrations'))
const PlatformIntegrationsRoute = React.lazy(() => import('@app/routes/main/platform-integrations'))
// Configuration（设置值 + 业务规则，main-api /system/config-entries/*）
const SettingsConfigurationRoute = React.lazy(() => import('@app/routes/main/settings-configuration'))
const PlatformConfigurationRoute = React.lazy(() => import('@app/routes/main/platform-configuration'))
const SettingsConfigurationRuleRoute = React.lazy(() => import('@app/routes/main/settings-configuration-rule'))
const SettingsConfigurationGlossaryRoute = React.lazy(() => import('@app/routes/main/settings-configuration-glossary'))
const PlatformConfigurationRuleRoute = React.lazy(() => import('@app/routes/main/platform-configuration-rule'))
const PlatformConfigurationGlossaryRoute = React.lazy(() => import('@app/routes/main/platform-configuration-glossary'))
const PlatformModelsRoute = React.lazy(() => import('@app/routes/main/platform-models'))
const PlatformAiUsageRoute = React.lazy(() => import('@app/routes/main/platform-ai-usage'))
const PlatformClassificationReviewRoute = React.lazy(() => import('@app/routes/main/platform-classification-review'))
const PlatformClassificationReviewRunsRoute = React.lazy(() => import('@app/routes/main/platform-classification-review-runs'))
const SettingsAiUsageRoute = React.lazy(() => import('@app/routes/main/settings-ai-usage'))
// Groups（Account Settings 下）：分组树 + 详情。设计 main-api 2026-10-21-group-hierarchy-design §8.3
const SettingsGroupsRoute = React.lazy(() => import('@app/routes/main/settings-groups'))
// 租户 MCP 连接器(main-api B6)
const AiConnectorsRoute = React.lazy(() => import('@app/routes/main/ai-connectors'))
const AiConnectorDetailRoute = React.lazy(() => import('@app/routes/main/ai-connectors/detail'))
// 平台自有 MCP 连接(main-api M3)：与租户页共用组件，只换作用域
const PlatformAiConnectorsRoute = React.lazy(() => import('@app/routes/main/platform-ai-connectors'))
const PlatformAiConnectorDetailRoute = React.lazy(() => import('@app/routes/main/platform-ai-connectors/detail'))
// AI Agent 工作台（租户空间）：/ai-agents，菜单 seed 由 main-api B1 出（Account Settings 下）
// 旧 Agent 工作台（ai-agents / agents / platform-ai-agents 三个 route 文件）的后端端点已在 main-api 删除；
// 路径由新的 Agents 目录 + Builder 接管（照抄 AgenticOS，main-api docs/superpowers/plans/2026-09-26-agenticos-frontend-plan.md）
const AgenticAgentsRoute = React.lazy(() => import('@app/routes/main/agentic-agents'))
const AgenticAgentBuilderRoute = React.lazy(() => import('@app/routes/main/agentic-agent-builder'))
const PlatformAgenticAgentsRoute = React.lazy(() => import('@app/routes/main/platform-agentic-agents'))
const PlatformAgenticAgentBuilderRoute = React.lazy(() => import('@app/routes/main/platform-agentic-agent-builder'))
const PlatformAiFunctionsRoute = React.lazy(() => import('@app/routes/main/platform-ai-functions'))
const PlatformPromptLibraryRoute = React.lazy(() => import('@app/routes/main/platform-prompt-library'))
const PlatformPromptLibraryDetailRoute = React.lazy(() => import('@app/routes/main/platform-prompt-library-detail'))
const PlatformAgenticEvalsRoute = React.lazy(() => import('@app/routes/main/platform-agentic-evals'))
const PlatformAiPresetsRoute = React.lazy(() => import('@app/routes/main/platform-ai-presets'))
const PlatformAiPresetDetailRoute = React.lazy(() => import('@app/routes/main/platform-ai-preset-detail'))
const PlatformAiEvalsReviewRoute = React.lazy(() => import('@app/routes/main/platform-ai-evals-review'))
const PlatformAiEvalsCandidatesRoute = React.lazy(() => import('@app/routes/main/platform-ai-evals-candidates'))
const PlatformAiEvalRunRoute = React.lazy(() => import('@app/routes/main/platform-ai-eval-run'))
const PlatformAiEvalCaseRoute = React.lazy(() => import('@app/routes/main/platform-ai-eval-case'))
const PlatformAiFunctionDetailRoute = React.lazy(() => import('@app/routes/main/platform-ai-function-detail'))
const PlatformJudgmentDetailRoute = React.lazy(() => import('@app/routes/main/platform-judgment-detail'))
// Agents 使用页：/agents，菜单 seed 由 main-api 出（人人可见，能用哪些 Agent 由 ACL 决定）
const RolesPermissionsPage = React.lazy(() => import('@app/routes/main/roles-permissions'))
const OwnersPage = React.lazy(() => import('@app/routes/main/owners'))
const OwnerDetailPage = React.lazy(() => import('@app/routes/main/owners/detail'))
const OwnerNewPage = React.lazy(() => import('@app/routes/main/owners/new'))
const OwnerReportsPage = React.lazy(() => import('@app/routes/main/owner-reports'))
const OwnerActivityPage = React.lazy(() => import('@app/routes/main/owner-activity'))
const OwnerLedgerPage = React.lazy(() => import('@app/routes/main/owner-ledger'))
const OwnerFixedFeesPage = React.lazy(() => import('@app/routes/main/owner-fixed-fees'))
const OwnerChargesPage = React.lazy(() => import('@app/routes/main/owner-charges'))
const OwnerDocumentsPage = React.lazy(() => import('@app/routes/main/owner-documents'))
const OwnerBankAccountsPage = React.lazy(() => import('@app/routes/main/owner-bank-accounts'))
const OwnerStatementsPage = React.lazy(() => import('@app/routes/main/owner-statements'))
const OwnerStatementSettingsPage = React.lazy(() => import('@app/routes/main/owner-statements-settings'))
const TenantsPage = React.lazy(() => import('@app/routes/main/tenants'))
const TenantDetailPage = React.lazy(() => import('@app/routes/main/tenants/detail'))
const TenantNewPage = React.lazy(() => import('@app/routes/main/tenants/new'))
const ViewsPage = React.lazy(() => import('@app/routes/main/views'))
// Sprint 5 — schema-driven ViewConfigPage 全面接管 /views/new、/views/:id、
// /views/:id/edit。老 ViewNewPage / ViewDetailPage / ViewEditRedirect 文件已删。
const ViewConfigPageV2 = React.lazy(() => import('@/features/view-config/ViewConfigPage'))
const GuestsPage = React.lazy(() => import('@app/routes/main/guests'))
const GuestDetailPage = React.lazy(() => import('@app/routes/main/guests/detail'))
const ReviewsPage = React.lazy(() => import('@app/routes/main/reviews'))
const ReviewDetailPage = React.lazy(() => import('@app/routes/main/reviews/detail'))
const KnowledgeBasePage = React.lazy(() => import('@app/routes/main/knowledge-base'))
const KnowledgeBaseDetailPage = React.lazy(() => import('@app/routes/main/knowledge-base/detail'))
const GuestGuidePage = React.lazy(() => import('@app/routes/main/guest-guide'))
const SettingsPage = React.lazy(() => import('@app/routes/main/settings'))
const ProfilePage = React.lazy(() => import('@app/routes/main/profile'))
const StaffAttendancePage = React.lazy(() => import('@app/routes/main/staff-attendance'))
const StaffAttendanceDetailPage = React.lazy(() => import('@app/routes/main/staff-attendance/detail'))
const JobsConfigPage = React.lazy(() => import('@app/routes/main/jobs-config'))
const JobsConsolePage = React.lazy(() => import('@app/routes/main/jobs-console'))
// Phase 3: TaskSubtypesConfigPage import removed — path now redirects to
// /task-configuration/subtypes. Legacy file remains on disk for the
// migration window in case we need to roll back.
const UpsellRequestsPage = React.lazy(() => import('@app/routes/main/upsell-requests'))
// Check Register — 银行对账（finance ledger + reconciliation groups）。
// slug 必须与后端菜单种子的 route_name / route_path=/check-register 完全一致。
const CheckRegisterPage = React.lazy(() => import('@app/routes/main/check-register'))
// Task Costing — 按 task 汇总费用成本的会计视图（spec 2026-09-09）。
// slug 必须与后端菜单种子的 route_name / route_path=/task-costing 完全一致。
const TaskCostingPage = React.lazy(() => import('@app/routes/main/task-costing'))
// Assessments — 房屋评估报表（main-api docs/assessment/API_CONTRACT.md §2/§3）。
// slug 必须与后端菜单种子的 route_name=Assessments / route_path=/assessments 一致，component_path=assessments/index。
const AssessmentsPage = React.lazy(() => import('@app/routes/main/assessments'))
// Smart Locks（一个菜单：By property / By door code，Settings 子页面）。只读 Nova 库，按 Refresh 才读厂商。
const SmartLocksPage = React.lazy(() => import('@/features/smart-locks/SmartLocksPage'))
const SmartLockSettingsPage = React.lazy(() => import('@/features/smart-locks/settings/SmartLockSettingsPage'))
const CodeHistoryPage = React.lazy(() => import('@/features/smart-locks/history/CodeHistoryPage'))
const ReservationActionsPage = React.lazy(() => import('@app/routes/main/reservation-actions'))
const CallLogsPage = React.lazy(() => import('@app/routes/main/call-logs'))
// Unified Call Routing page — merges the former /phone-numbers (provisioned
// numbers list + add/release) and /call-routing-settings (per-number ring
// levels / rules / voicemail) into one page. Both old routes now redirect here.
const CallRoutingPage = React.lazy(() => import('@app/routes/main/call-routing'))
const ReservationReadinessBoardPage = React.lazy(() => import('@app/routes/main/reservation-readiness-board'))
const ShiftRequestsPage = React.lazy(() => import('@app/routes/main/shift-requests'))
const AttendanceRequestsPage = React.lazy(() => import('@app/routes/main/attendance-requests'))
const ShiftsPage = React.lazy(() => import('@app/routes/main/shifts'))
const MyShiftsPage = React.lazy(() => import('@app/routes/main/my-shifts'))
const ProcurementPage = React.lazy(() => import('@app/routes/main/procurement'))
const ProcurementDetailPage = React.lazy(() => import('@app/routes/main/procurement/detail'))
const InventoryListPage = React.lazy(() => import('@app/routes/main/asset-mgr/inventory'))
const InventoryTransactionsPage = React.lazy(() => import('@app/routes/main/asset-mgr/transactions'))
const ProductsListPage = React.lazy(() => import('@app/routes/main/asset-mgr/products'))
const CategoriesListPage = React.lazy(() => import('@app/routes/main/asset-mgr/categories'))
const OwnershipsListPage = React.lazy(() => import('@app/routes/main/asset-mgr/ownerships'))
const SystemTagsPage = React.lazy(() => import('@app/routes/main/system/tags'))
const SystemActivityEventTypesPage = React.lazy(() => import('@app/routes/main/system/activity-event-types'))
const OpsEventBusPage = React.lazy(() => import('@app/routes/main/ops/eventbus'))
const AssetsListPage = React.lazy(() => import('@app/routes/main/asset-mgr/assets'))
const AssetDashboardPage = React.lazy(() => import('@app/routes/main/asset-mgr/dashboard'))
const MaterialRequestsPage = React.lazy(() => import('@app/routes/main/material-requests'))
const TaskMaterialsPage = React.lazy(() => import('@app/routes/main/task-materials'))
const TaskTemplatesPage = React.lazy(() => import('@app/routes/main/task-templates'))
const TaskTemplateEditorPage = React.lazy(() => import('@/components/task-templates/TaskTemplateEditor'))
const TaskTemplateBatchCreatePage = React.lazy(() => import('@/components/task-templates/BatchCreatePage'))
const WorkflowsPage = React.lazy(() => import('@app/routes/main/workflows'))
const WorkflowEditorPage = React.lazy(() => import('@app/routes/main/workflow-editor'))
const WorkflowRunsPage = React.lazy(() => import('@app/routes/main/workflow-runs'))

// Catalog Hub (/catalogs) — generic catalog framework + reason/subtype/task-type
// special routes + deprecated cat/subcat. Supersedes /task-configuration.
const CatalogsLayout = React.lazy(() => import('@app/routes/main/catalogs/_layout'))
const GenericCatalogRoute = React.lazy(() => import('@app/routes/main/catalogs/GenericCatalogRoute'))
const CatalogReasonsPage = React.lazy(() => import('@app/routes/main/catalogs/resolution-reasons'))
const CatalogSubtypesPage = React.lazy(() => import('@app/routes/main/catalogs/task-subtypes'))
const CatalogTaskTypesPage = React.lazy(() => import('@app/routes/main/catalogs/task-types'))

export const router = createBrowserRouter([
  {
    path: "/debug/clerk-token",
    element: React.createElement(ClerkTokenDebuggerPage),
  },
  {
    path: "/login",
    element: React.createElement(AuthGuard, { requireAuth: false, children: React.createElement(LoginClerkPage) }),
  },
  {
    path: "/register",
    element: React.createElement(AuthGuard, { requireAuth: false, children: React.createElement(RegisterClerkPage) }),
  },
  {
    path: "/onboarding/organization",
    element: React.createElement(AuthGuard, {
      requireAuth: true,
      allowOnboarding: true,
      children: React.createElement(OrganizationOnboardingPage)
    }),
  },
  {
    path: "/forgot-password",
    element: React.createElement(AuthGuard, { requireAuth: false, children: React.createElement(ForgotPasswordPage) }),
  },
  {
    path: "/auth/ticket-login",
    element: React.createElement(TicketLoginPage),
  },
  {
    // token 即授权 —— 刻意不套 AuthGuard,匿名访客必须能直接打开。
    // 契约见 docs/superpowers/specs/2026-08-28-mcp-attachments-design.md §3.1。
    path: "/upload/:token",
    element: React.createElement(UploadLandingPage),
  },
  {
    // 外部承包商工单页 —— 同 /upload/:token:token 即授权,刻意不套 AuthGuard,页面不碰登录态。
    // 契约见 docs: contractor-link API 契约 §3(公开端点 /external/contractor-links/{token})。
    path: "/job/:token",
    element: React.createElement(ContractorJobPage),
  },
  {
    // 刻意不套 AuthGuard:浏览器从第三方授权页经 main-api 回调 302 过来,页面只转交结果、不读登录态。
    // 协议见 src/features/ai-connectors/oauthPopup.ts。
    path: "/oauth/mcp/callback",
    element: React.createElement(McpOAuthCallbackPage),
  },
  {
    path: "/logout",
    element: React.createElement(Navigate, { to: "/login", replace: true }),
  },
  {
    path: "/",
    element: React.createElement(RootRedirect),
  },
  {
    path: "/",
    element: React.createElement(AuthGuard, {
      requireAuth: true,
      children: React.createElement(Layout)
    }),
    children: [
      {
        path: "dashboard",
        element: React.createElement(DashboardPage),
      },
      {
        path: "announcements",
        element: React.createElement(AnnouncementsHistoryPage),
      },
      {
        // 与 academy/admin 同构(功能在前);避免站内不存在的 /admin 顶层前缀
        path: "announcements/admin",
        element: React.createElement(AdminAnnouncementsPage),
      },
      // Academy（学员页全员可见,不挂路由门;admin 由 <Can> 在组件内门控）
      {
        path: "academy",
        element: React.createElement(AcademyPage),
      },
      {
        path: "academy/admin",
        element: React.createElement(AcademyAdminPage),
      },
      {
        path: "academy/:courseId",
        element: React.createElement(AcademyCourseDetailPage),
      },
      {
        path: "academy/:courseId/lessons/:lessonId",
        element: React.createElement(AcademyLessonPlayerPage),
      },
      {
        path: "inbox",
        element: React.createElement(InboxPage),
        handle: { requiredCapabilities: ['messaging.view'] },
      },
      {
        // 内部即时消息（同事之间单聊 / 群聊）；入口在顶栏，不在菜单。
        // 可选的 :id 让列表与会话同一个路由元素：点开会话不重挂整页（左栏列表不闪）
        path: "messages/:id?",
        element: React.createElement(MessagesPage),
        handle: { requiredCapabilities: ['chat.use'] },
      },
      {
        path: "intents",
        element: React.createElement(IntentsPage),
        handle: { requiredCapabilities: ['issues.view'] },
      },
      {
        // 2026-08-17 起页面内容是 task triage 副本(task 控制台),能力清单对齐 /task-triage
        path: "intake-requests",
        element: React.createElement(IntakeRequestsPage),
        handle: { requiredCapabilities: ['tasks.view'] },
      },
      {
        path: "intents/:id",
        element: React.createElement(IntentDetailPage),
      },
      {
        path: "tasks",
        element: React.createElement(TasksPage),
        handle: { requiredCapabilities: ['tasks.view'] },
      },
      {
        // Customer Service > Support Cases
        // 跟 /tasks 同辈 (不嵌套), 表达独立 page 的 UX 意图 (URL 不出现 'tasks')
        path: "support-cases",
        element: React.createElement(SupportCasesPage),
        handle: { requiredCapabilities: ['tasks.view'] },
      },
      {
        // 个人工作台: 只看 assign 给当前用户的 task (页面级 scopeFilter)
        path: "my-tasks",
        element: React.createElement(MyTasksPage),
        handle: { requiredCapabilities: ['tasks.view'] },
      },
      {
        path: "task-triage",
        element: React.createElement(TaskTriagePage),
        handle: { requiredCapabilities: ['tasks.view'] },
      },
      {
        path: "tasks/:id",
        element: React.createElement(TaskDetailPage),
      },
      {
        path: "task-calendar",
        element: React.createElement(TaskCalendarRoute),
        handle: { requiredCapabilities: ['tasks.view'] },
      },
      {
        path: "resource-map-view",
        element: React.createElement(ResourceMapViewPage),
        handle: { requiredCapabilities: ['tasks.view'] },
      },
      {
        path: "listings",
        element: React.createElement(ListingsPage),
        handle: { requiredCapabilities: ['listings.view'] },
      },
      {
        path: "listings/:id",
        element: React.createElement(ListingsDetailPage),
      },
      {
        path: "reservations",
        element: React.createElement(ReservationsPage),
        handle: { requiredCapabilities: ['reservations.view'] },
      },
      {
        path: "reservations/:id",
        element: React.createElement(ReservationDetailPage),
      },
      {
        path: "users",
        element: React.createElement(UsersPage),
        handle: { requiredCapabilities: ['config.users'] },
      },
      {
        path: "users/:id",
        element: React.createElement(UserDetailPage),
      },
      {
        path: "roles",
        element: React.createElement(Navigate, { to: "/roles-permissions", replace: true }),
      },
      {
        // 旧 Access Control(v1) 已退役，统一重定向到新的 Roles & Permissions 页
        path: "access-control",
        element: React.createElement(Navigate, { to: "/roles-permissions", replace: true }),
      },
      {
        // 旧 Access Control v2 已退役，统一重定向到新的 Roles & Permissions 页
        path: "access-control-v2",
        element: React.createElement(Navigate, { to: "/roles-permissions", replace: true }),
      },
      {
        path: "roles-permissions",
        element: React.createElement(RolesPermissionsPage),
        handle: { requiredCapabilities: ['config.roles'] },
      },
      {
        path: "task-kinds",
        element: React.createElement(TaskKindsRoute),
        handle: { requiredCapabilities: ['config.ai'] },  // Task Kinds:目录只读(定义只改 seeds),租户管别名 / 技能 / 知识绑定;后端权限 ai:config:read / update
      },
      {
        // 旧角色详情编辑器已退役：它直写 role_permission，会被 capability 派生覆盖，
        // 也不认 capability（如新建角色默认的 chat.use）。统一重定向到 Roles & Permissions
        path: "roles/:id",
        element: React.createElement(Navigate, { to: "/roles-permissions", replace: true }),
      },
      {
        path: "owners",
        element: React.createElement(OwnersPage),
        handle: { requiredCapabilities: ['owners.view'] },
      },
      {
        path: "owners/:id",
        element: React.createElement(OwnerDetailPage),
      },
      {
        path: "owners/new",
        element: React.createElement(OwnerNewPage),
      },
      {
        // Owner Portal 业主报告（设计文档 §5.2 P-2）。⚠️ 侧栏入口靠后端 sys.menu 种子
        // （route_path=/owner-reports）+ role_menu 授权；FE 这里只登记路由。
        // 闸门 owner_portal.view（含 owner_portal:report:read），只供角色编辑器，不拦截。
        path: "owner-reports",
        element: React.createElement(OwnerReportsPage),
        handle: { requiredCapabilities: ['owner_portal.view'] },
      },
      {
        // 员工端 Owner Activity（跨工单的业主动态，2026-10-30）。侧栏入口靠后端 sys.menu 种子
        // （route_path=/owner-activity，Owner Approvals 正下方）+ role_menu。闸门 owner_portal.view
        // （含 owner_portal:report:read），只供角色编辑器，不拦截；行内回复另看 owner_portal:report_message:create。
        path: "owner-activity",
        element: React.createElement(OwnerActivityPage),
        handle: { requiredCapabilities: ['owner_portal.view'] },
      },
      {
        // 2026-10-20 Owner Stays 并入 Reservations（用户拍板）：独立页面与菜单撤掉，业主自住就是 Reservations 里的一行
        // （业主提交时建 pending_confirmation 预订），在预订详情里确认 / 拒绝。旧链接（书签、旧通知）跳到 Pending 系统视图。
        path: "owner-stays",
        element: React.createElement(Navigate, { to: PENDING_OWNER_STAYS_PATH, replace: true }),
      },
      {
        // Owner Portal 账务（切片 S4-a，设计文档 §5.2 P-5 / P-6）。侧栏入口同样靠后端 sys.menu 种子
        // （route_path=/owner-ledger、/owner-fixed-fees）+ role_menu。闸门只供角色编辑器，不拦截；
        // Recalculate 按钮另看 owner_portal:ledger:sync，Fixed Fee 写入看 owner_portal:fixed_fee:create/update。
        path: "owner-ledger",
        element: React.createElement(OwnerLedgerPage),
        handle: { requiredCapabilities: ['owner_portal.view'] },
      },
      {
        path: "owner-fixed-fees",
        element: React.createElement(OwnerFixedFeesPage),
        handle: { requiredCapabilities: ['owner_portal.view'] },
      },
      {
        // Owner Charges：月底把 Task 费用批量转成业主扣款（S4-b 子切片）。侧栏入口靠后端 sys.menu 种子
        // （route_path=/owner-charges，父菜单 Accounting & HR）+ role_menu。闸门 owner_portal.view（含 owner_portal:charge:read），
        // 只供角色编辑器，不拦截；Convert / Void 另看 owner_portal:charge:convert / owner_portal:charge:void（capability owner_portal.charge_manage）。
        path: "owner-charges",
        element: React.createElement(OwnerChargesPage),
        handle: { requiredCapabilities: ['owner_portal.view'] },
      },
      {
        // Owner Documents：业主文档（1099 / 水电账单 / 报价）。侧栏入口靠后端 sys.menu 种子（route_path=/owner-documents，
        // Owners 组）+ role_menu。闸门 owner_portal.view（含 owner_portal:document:read），只供角色编辑器，不拦截；
        // 新增 / 编辑 / 删除另看 owner_portal:document:manage（capability owner_portal.document_manage）。
        path: "owner-documents",
        element: React.createElement(OwnerDocumentsPage),
        handle: { requiredCapabilities: ['owner_portal.view'] },
      },
      {
        // Bank Accounts：公司收付款账户（cash account）+ 业主收款账户 + 业主银行 CSV 导入（mainapp-api-new
        // feat/owner-bank-accounts CONTRACT-bank.md）。侧栏入口靠后端 sys.menu 种子（route_path=/owner-bank-accounts，Owners 组）
        // + role_menu。闸门 owner_portal.view（含 owner_portal:bank_account:read），只供角色编辑器，不拦截；新建 / 编辑 / 删除 / 导入
        // 看 owner_portal:bank_account:manage（owner_portal.bank_account_manage），明文账号看 owner_portal:bank_account:reveal。
        path: "owner-bank-accounts",
        element: React.createElement(OwnerBankAccountsPage),
        handle: { requiredCapabilities: ['owner_portal.view'] },
      },
      {
        // Owner Statements：月度业主对账单（S4-b，设计 §5.2 P-4）。侧栏入口靠后端 sys.menu 种子（route_path=/owner-statements，
        // 父菜单 Accounting & HR）+ role_menu。闸门 owner_portal.view（含 owner_portal:statement:read），只供角色编辑器，不拦截；
        // Generate / Regenerate / 调整行看 owner_portal.statement_manage，Issue / Mark paid 看 owner_portal.statement_issue。
        path: "owner-statements",
        element: React.createElement(OwnerStatementsPage),
        handle: { requiredCapabilities: ['owner_portal.view'] },
      },
      {
        // Customize Owner statement（2026-10-27，原 Statement settings 弹窗改整页）：从 Owner Statements 页头的
        // 「Statement settings」进。闸门与列表页同（owner_portal.view，含 owner_portal:statement:read）；存另看
        // owner_portal:statement:update（capability owner_portal.statement_manage），页面内门控。
        path: "owner-statements/settings",
        element: React.createElement(OwnerStatementSettingsPage),
        handle: { requiredCapabilities: ['owner_portal.view'] },
      },
      {
        path: "tenants",
        element: React.createElement(TenantsPage),
        handle: { requiredCapabilities: ['platform.admin'] },  // platform-only(仅 superuser);菜单门已认 platform tier(决策 C)
      },
      {
        path: "tenants/:id",
        element: React.createElement(TenantDetailPage),
      },
      {
        path: "tenants/new",
        element: React.createElement(TenantNewPage),
      },
      {
        path: "views",
        element: React.createElement(ViewsPage),
        // 个人视图(base.personal_views 是 base 地板,不入 role_capability)→ 不挂路由门(人人可见)。
      },
      {
        path: "views/new",
        element: React.createElement(ViewConfigPageV2, { mode: 'create' }),
      },
      {
        path: "views/:id",
        element: React.createElement(ViewConfigPageV2, { mode: 'edit' }),
      },
      {
        path: "views/:id/edit",
        element: React.createElement(ViewConfigPageV2, { mode: 'edit' }),
      },
      {
        path: "guests",
        element: React.createElement(GuestsPage),
        handle: { requiredCapabilities: ['guests.view'] },
      },
      {
        path: "guests/:id",
        element: React.createElement(GuestDetailPage),
      },
      {
        path: "reviews",
        element: React.createElement(ReviewsPage),
        handle: { requiredCapabilities: ['reviews.view'] },
      },
      {
        path: "reviews/:id",
        element: React.createElement(ReviewDetailPage),
      },
      {
        path: "knowledge-base",
        element: React.createElement(KnowledgeBasePage),
        handle: { requiredCapabilities: ['knowledge.view'] },
      },
      {
        // KB 文章独立详情页（正文 + 详情侧栏）。AI Assistant 来源链接与 flyout 的复制深链都落这里。
        path: "knowledge-base/:id",
        element: React.createElement(KnowledgeBaseDetailPage),
        handle: { requiredCapabilities: ['knowledge.view'] },
      },
      {
        path: "guest-guide",
        element: React.createElement(GuestGuidePage),
        handle: { requiredCapabilities: ['guest_guide.view'] },
      },
      {
        path: "settings",
        element: React.createElement(SettingsPage),
        handle: { requiredCapabilities: ['config.tenant_settings'] },
      },
      {
        path: "settings/vault",
        element: React.createElement(SettingsVaultRoute),
        handle: { requiredCapabilities: ['config.ai_vault_view'] },  // 租户 Vault：/ai/vault/*（ai:secret:read / manage）
      },
      {
        path: "settings/models",
        element: React.createElement(SettingsModelsRoute),
        handle: { requiredCapabilities: ['config.ai_vault_view'] },  // 租户 saved models：/ai/model-profiles*（ai:model_profile:*）
      },
      {
        path: "settings/integrations",
        element: React.createElement(SettingsIntegrationsRoute),
        handle: { requiredCapabilities: ['config.integrations_view'] },  // 租户 Integrations：/integrations/*（integration:connection:read）
      },
      {
        // Integrations「Connect with <厂商>」的回调：要登录（后端核对发起人），权限由后端 complete 端点判
        path: "oauth/callback",
        element: React.createElement(IntegrationOAuthCallbackRoute),
      },
      {
        path: "platform/integrations",
        element: React.createElement(PlatformIntegrationsRoute),
        handle: { requiredCapabilities: ['platform.admin'] },  // 平台 Integrations：/platform/integrations/*（system:integration:read，platform.admin 覆盖）
      },
      {
        path: "settings/configuration",
        element: React.createElement(SettingsConfigurationRoute),
        handle: { requiredCapabilities: ['config.settings_values', 'config.business_rules_view'] },  // 租户 Configuration：Settings（system:config_entry:read / update）+ Business rules 页签（system:business_rule:read）
      },
      {
        path: "settings/configuration/glossary",
        element: React.createElement(SettingsConfigurationGlossaryRoute),
        handle: { requiredCapabilities: ['config.business_rules_view'] },  // 租户术语表：/system/business-rules/glossary（read；写在 config.business_rules_manage）
      },
      {
        path: "settings/configuration/families/:family/entries/:handle",
        element: React.createElement(SettingsConfigurationRuleRoute),
        handle: { requiredCapabilities: ['config.business_rules_view'] },  // 业务规则条目：/system/business-rules/families/{family}/entries/{handle}（read / trial；写在 config.business_rules_manage）
      },
      {
        path: "settings/configuration/rules/:handle",
        element: React.createElement(SettingsConfigurationRuleRoute),
        handle: { requiredCapabilities: ['config.business_rules_view'] },  // 业务规则详情：/system/business-rules/{handle}（read / trial；写在 config.business_rules_manage）
      },
      {
        path: "platform/configuration",
        element: React.createElement(PlatformConfigurationRoute),
        handle: { requiredCapabilities: ['platform.config_values', 'platform.business_rules'] },  // 平台 Configuration：?scope=platform + PUT platform-value（system:config_entry:update_platform）+ 平台默认规则
      },
      {
        path: "platform/configuration/glossary",
        element: React.createElement(PlatformConfigurationGlossaryRoute),
        handle: { requiredCapabilities: ['platform.business_rules'] },  // 平台术语表：/system/business-rules/glossary?scope=platform（写要 system:business_rule:update_platform）；每个公司都用
      },
      {
        path: "platform/configuration/families/:family/entries/:handle",
        element: React.createElement(PlatformConfigurationRuleRoute),
        handle: { requiredCapabilities: ['platform.business_rules'] },  // 平台条目（示例）：…/families/{family}/entries/{handle}?scope=platform
      },
      {
        path: "platform/configuration/rules/:handle",
        element: React.createElement(PlatformConfigurationRuleRoute),
        handle: { requiredCapabilities: ['platform.business_rules'] },  // 平台默认规则详情：/system/business-rules/{handle}?scope=platform（system:business_rule:update_platform）
      },
      {
        path: "platform/vault",
        element: React.createElement(PlatformVaultRoute),
        handle: { requiredCapabilities: ['platform.ai_operate'] },  // 平台 › AI（Frank 2026-10-06：平台用户能看到就能用）
      },
      {
        path: "platform/models",
        element: React.createElement(PlatformModelsRoute),
        handle: { requiredCapabilities: ['platform.ai_operate'] },  // 平台 › AI（Frank 2026-10-06：平台用户能看到就能用）
      },
      {
        path: "ai-connectors",
        element: React.createElement(AiConnectorsRoute),
        handle: { requiredCapabilities: ['ai.mcp_connector'] },  // 租户 MCP 连接器(ai.yaml:ai:mcp:read/manage;自填 URL 另需 ai:mcp:custom_url);只调 /ai/mcp/*
      },
      {
        path: "ai-connectors/:id",
        element: React.createElement(AiConnectorDetailRoute),
        handle: { requiredCapabilities: ['ai.mcp_connector'] },
      },
      {
        // 旧「使用画廊」已随旧工作台退役：书签跳到新目录
        path: "agents",
        element: React.createElement(Navigate, { to: "/ai-agents", replace: true }),
      },
      {
        path: "ai-agents",
        element: React.createElement(AgenticAgentsRoute),
        handle: { requiredCapabilities: ['ai.agent_use'] },  // Agents 目录（ai:agent:read；新建 / 编辑另需 ai.agent_author）
      },
      {
        path: "ai-agents/:id",
        element: React.createElement(AgenticAgentBuilderRoute),
        handle: { requiredCapabilities: ['ai.agent_use'] },  // Builder（编辑 ai:agent:create、发布 ai:agent:publish、试跑 ai:agent:run）
      },
      {
        path: "platform/ai-connectors",
        element: React.createElement(PlatformAiConnectorsRoute),
        handle: { requiredCapabilities: ['platform.ai_operate'] },  // 平台 › AI（Frank 2026-10-06：平台用户能看到就能用）
      },
      {
        path: "platform/ai-connectors/:id",
        element: React.createElement(PlatformAiConnectorDetailRoute),
        handle: { requiredCapabilities: ['platform.ai_operate'] },  // 平台 › AI（Frank 2026-10-06：平台用户能看到就能用）
      },
      {
        // 旧的独立对话页已下线（对话一律在助手浮窗里）。main-api 删掉「Agentic Chat」菜单之前，点旧菜单落到 Agent 目录，从卡片 Start chat
        path: "ai-chat",
        element: React.createElement(Navigate, { to: "/ai-agents", replace: true }),
      },
      {
        path: "platform/ai-agents",
        element: React.createElement(PlatformAgenticAgentsRoute),
        handle: { requiredCapabilities: ['platform.ai_operate'] },  // 平台 › AI（Frank 2026-10-06：平台用户能看到就能用）
      },
      {
        path: "platform/ai-agents/:id",
        element: React.createElement(PlatformAgenticAgentBuilderRoute),
        handle: { requiredCapabilities: ['platform.ai_operate'] },  // 平台 › AI（Frank 2026-10-06：平台用户能看到就能用）
      },
      {
        // 预设（助手对话框的快捷按钮）：/platform/ai/presets*（ai:platform_agentic:manage）
        path: "platform/ai-presets",
        element: React.createElement(PlatformAiPresetsRoute),
        handle: { requiredCapabilities: ['platform.ai_operate'] },  // 平台 › AI（Frank 2026-10-06：平台用户能看到就能用）
      },
      {
        path: "platform/ai-presets/:key",
        element: React.createElement(PlatformAiPresetDetailRoute),
        handle: { requiredCapabilities: ['platform.ai_operate'] },  // 平台 › AI（Frank 2026-10-06：平台用户能看到就能用）
      },
      {
        path: "platform/ai-evals",
        element: React.createElement(PlatformAgenticEvalsRoute),
        handle: { requiredCapabilities: ['evaluation.agent_case_review'] },
      },
      {
        path: "platform/ai-evals/review",
        element: React.createElement(PlatformAiEvalsReviewRoute),
        handle: { requiredCapabilities: ['evaluation.agent_case_review'] },
      },
      {
        path: "platform/ai-evals/candidates",
        element: React.createElement(PlatformAiEvalsCandidatesRoute),
        handle: { requiredCapabilities: ['evaluation.agent_case_review'] },
      },
      {
        path: "platform/ai-evals/runs/:id",
        element: React.createElement(PlatformAiEvalRunRoute),
        handle: { requiredCapabilities: ['evaluation.agent_case_review'] },
      },
      {
        path: "platform/ai-evals/cases/:id",
        element: React.createElement(PlatformAiEvalCaseRoute),
        handle: { requiredCapabilities: ['evaluation.agent_case_review'] },
      },
      {
        // Prompts = Prompt 库（取代 Skills + Context）：/platform/ai/prompt-assets*（ai:platform_agentic:manage）
        path: "platform/ai-prompts",
        element: React.createElement(PlatformPromptLibraryRoute),
        handle: { requiredCapabilities: ['platform.ai_operate'] },  // 平台 › AI（Frank 2026-10-06：平台用户能看到就能用）
      },
      {
        path: "platform/ai-prompts/:id",
        element: React.createElement(PlatformPromptLibraryDetailRoute),
        handle: { requiredCapabilities: ['platform.ai_operate'] },  // 平台 › AI（Frank 2026-10-06：平台用户能看到就能用）
      },
      {
        // AI 函数（原 Prompts 目录）：/platform/ai/prompts（ai:platform_agentic:manage）
        path: "platform/ai-functions",
        element: React.createElement(PlatformAiFunctionsRoute),
        handle: { requiredCapabilities: ['platform.ai_operate'] },  // 平台 › AI（Frank 2026-10-06：平台用户能看到就能用）
      },
      {
        path: "platform/ai-functions/:namespace/:name",
        element: React.createElement(PlatformAiFunctionDetailRoute),
        handle: { requiredCapabilities: ['platform.ai_operate'] },  // 平台 › AI（Frank 2026-10-06：平台用户能看到就能用）
      },
      {
        // 判断型 AI 函数：slug 是一段（Prompt 型是 ns/name 两段）。/platform/ai/judgments/{slug}
        path: "platform/ai-functions/:slug",
        element: React.createElement(PlatformJudgmentDetailRoute),
        handle: { requiredCapabilities: ['platform.ai_operate'] },  // 平台 › AI（Frank 2026-10-06：平台用户能看到就能用）
      },
      {
        // 旧 AI Tool 工作台随旧 Agent 工作台退役（main-api 2026-09-26 删了 /ai/tool-defs/* 等端点，菜单也删了）：
        // 书签 / 旧链接跳到新的 Agents；业务工具现在是 Builder → Toolbox 里的业务包
        path: "ai-tools/*",
        element: React.createElement(Navigate, { to: "/ai-agents", replace: true }),
      },
      {
        path: "ai-tools",
        element: React.createElement(Navigate, { to: "/ai-agents", replace: true }),
      },
      {
        // 任务分类复核（main-api 设计 2026-10-19-task-classification-review）：/ai/classification-review/*
        // 复核 = ai:classification_review:read/decide/run；回写另需 platform.classification_writeback，批准新种类另需 ai:task_kind:approve（页内按钮门）
        path: "platform/classification-review",
        element: React.createElement(PlatformClassificationReviewRoute),
        handle: { requiredCapabilities: ['platform.classification_review'] },
      },
      {
        path: "platform/classification-review/runs",
        element: React.createElement(PlatformClassificationReviewRunsRoute),
        handle: { requiredCapabilities: ['platform.classification_review'] },
      },
      {
        path: "platform/ai-usage",
        element: React.createElement(PlatformAiUsageRoute),
        handle: { requiredCapabilities: ['platform.ai_operate'] },  // 平台 › AI（Frank 2026-10-06：平台用户能看到就能用）
      },
      {
        // 旧详情页已并进 Breakdown 的 flyout：保留重定向给书签
        path: "platform/ai-usage/tenants/:tenantId",
        element: React.createElement(PlatformAiUsageTenantRedirect),
      },
      {
        path: "platform/ai-usage/models/:provider/:id",
        element: React.createElement(Navigate, { to: "/platform/ai-usage?tab=breakdown&dim=model", replace: true }),
      },
      // 改版删掉的平台 AI 页：旧书签 / 库里没下线的旧菜单 → 新家
      ...LEGACY_PLATFORM_AI_REDIRECTS.map((r) => ({ path: r.path, element: React.createElement(LegacyRedirect, { to: r.to }) })),
      // 2026-10-16 旧菜单清理删掉的 Account Settings 页 / Features 页：旧书签 → 新家
      ...LEGACY_TENANT_SETTINGS_REDIRECTS.map((r) => ({ path: r.path, element: React.createElement(LegacyRedirect, { to: r.to }) })),
      {
        path: "settings/groups",
        element: React.createElement(SettingsGroupsRoute),
        handle: { requiredCapabilities: ['groups.view'] },  // 读 pms:group:read；写按钮按 pms:group:create/update/delete、manage_workarea 门控
      },
      {
        path: "settings/groups/:id",
        element: React.createElement(SettingsGroupsRoute),
        handle: { requiredCapabilities: ['groups.view'] },
      },
      {
        path: "settings/ai-usage",
        element: React.createElement(SettingsAiUsageRoute),
        handle: { requiredCapabilities: ['ai.usage_view'] },  // 租户 Usage & Billing:POST /ai/usage-events/search(ai:usage:read,只看本租户)
      },
      {
        path: "profile",
        element: React.createElement(ProfilePage),
      },
      {
        path: "staff-attendance",
        element: React.createElement(StaffAttendancePage),
        handle: { requiredCapabilities: ['attendance.view'] },
      },
      {
        path: "staff-attendance/:id",
        element: React.createElement(StaffAttendanceDetailPage),
      },
      {
        path: "jobs-config",
        element: React.createElement(JobsConfigPage),
        handle: { requiredCapabilities: ['platform.admin'] },  // platform-only(仅 superuser)
      },
      {
        path: "jobs-console",
        element: React.createElement(JobsConsolePage),
        handle: { requiredCapabilities: ['platform.admin'] },  // platform-only(仅 superuser)
      },
      {
        // Phase 3 migration: /task-subtypes-config → /task-configuration/subtypes.
        // Kept as a redirect for ~4 weeks for any external bookmarks; remove after.
        path: "task-subtypes-config",
        element: React.createElement(Navigate, {
          to: "/catalogs/task-subtypes",
          replace: true,
        }),
      },
      {
        // Legacy: /workflow-stages-config → /catalogs/workflow-stages.
        // Kept as a redirect for external bookmarks; remove after migration window.
        path: "workflow-stages-config",
        element: React.createElement(Navigate, {
          to: "/catalogs/workflow-stages",
          replace: true,
        }),
      },
      {
        // Catalog Hub (/catalogs) — supersedes /task-configuration. Generic catalog
        // pages (skills/positions/service-lines/workflow-stages) via parameterized
        // route; reason/subtype/task-type special routes.
        path: "catalogs",
        element: React.createElement(CatalogsLayout),
        children: [
          { index: true, element: React.createElement(Navigate, { to: "skills", replace: true }) },
          { path: "resolution-reasons",    element: React.createElement(CatalogReasonsPage),  handle: { requiredCapabilities: ['config.task_catalog'] } },
          { path: "task-subtypes",         element: React.createElement(CatalogSubtypesPage),  handle: { requiredCapabilities: ['config.task_catalog'] } },
          { path: "task-types",            element: React.createElement(CatalogTaskTypesPage), handle: { requiredCapabilities: ['config.task_catalog'] } },
          // generic(skills/positions/service-lines/workflow-stages)走参数化路由,放最后兜底
          { path: ":catalogKey", element: React.createElement(GenericCatalogRoute) },
        ],
      },
      // Legacy /task-configuration/* → /catalogs/* redirects (external bookmarks).
      { path: "task-configuration",                 element: React.createElement(Navigate, { to: "/catalogs", replace: true }) },
      { path: "task-configuration/reasons",         element: React.createElement(Navigate, { to: "/catalogs/resolution-reasons", replace: true }) },
      { path: "task-configuration/subtypes",        element: React.createElement(Navigate, { to: "/catalogs/task-subtypes", replace: true }) },
      { path: "task-configuration/task-types",      element: React.createElement(Navigate, { to: "/catalogs/task-types", replace: true }) },
      // /guest-portal(+/:propertyId) 旧版每房源编辑器已退役(2026-08-13):
      // dev/prod 菜单均早已只挂新版 /guest-guide,且旧页面的按房源可见性过滤
      // 依赖后端从不返回的 listingScope/targetListingIds,恒 fails-open。
      { path: "guest-portal", element: React.createElement(Navigate, { to: "/guest-guide", replace: true }) },
      { path: "guest-portal/:propertyId", element: React.createElement(Navigate, { to: "/guest-guide", replace: true }) },
      {
        path: "upsell-requests",
        element: React.createElement(UpsellRequestsPage),
        handle: { requiredCapabilities: ['upsell.view'] },
      },
      {
        // 进页面只要求"能看对账组"；建组 / 对账在页面内按 permission code 再分。
        path: "check-register",
        element: React.createElement(CheckRegisterPage),
        handle: { requiredCapabilities: ['finance.reconciliation_view'] },
      },
      {
        // Task Costing（2026-09-09）：只读看板，页内没有写动作，只要 view capability。
        path: "task-costing",
        element: React.createElement(TaskCostingPage),
        handle: { requiredCapabilities: ['finance.task_costing_view'] },
      },
      {
        // 房屋评估报表：只读，页内的作废 / 重填在任务详情卡片上做，不在这里。
        path: "assessments",
        element: React.createElement(AssessmentsPage),
        handle: { requiredCapabilities: ['assessments.view'] },
      },
      {
        path: "smart-locks",
        element: React.createElement(SmartLocksPage),
        handle: { requiredCapabilities: ['smart_devices.view'] },
      },
      {
        path: "smart-locks/settings",
        element: React.createElement(SmartLockSettingsPage),
        handle: { requiredCapabilities: ['smart_devices.settings'] },
      },
      {
        path: "smart-locks/history",
        element: React.createElement(CodeHistoryPage),
        handle: { requiredCapabilities: ['smart_devices.view'] },
      },
      {
        path: "reservation-actions",
        element: React.createElement(ReservationActionsPage),
        handle: { requiredCapabilities: ['reservations.view'] },
      },
      {
        path: "call-logs",
        element: React.createElement(CallLogsPage),
      },
      {
        // Unified page. The backend sys.menu row points here (main-api migration
        // e6a1c9d47b2f moved it off /call-routing-settings, 2026-10-16).
        path: "call-routing",
        element: React.createElement(CallRoutingPage),
      },
      {
        // Legacy paths → unified page. Kept as redirects for existing bookmarks.
        path: "call-routing-settings",
        element: React.createElement(Navigate, { to: "/call-routing", replace: true }),
      },
      {
        path: "phone-numbers",
        element: React.createElement(Navigate, { to: "/call-routing", replace: true }),
      },
      {
        // 旧 readiness 列表页已退役(2026-07-08 Phase B,物化表读时对账化);
        // 书签直链重定向到新看板。
        path: "reservation-readiness",
        element: React.createElement(Navigate, { to: "/reservation-readiness-board", replace: true }),
      },
      {
        path: "reservation-readiness-board",
        element: React.createElement(ReservationReadinessBoardPage),
        handle: { requiredCapabilities: ['reservations.view'] },
      },
      {
        path: "shift-requests",
        element: React.createElement(ShiftRequestsPage),
        handle: { requiredCapabilities: ['scheduling.self_service'] },
      },
      {
        path: "attendance-requests",
        element: React.createElement(AttendanceRequestsPage),
        handle: { requiredCapabilities: ['attendance-change.approve'] },
      },
      {
        path: "shifts",
        element: React.createElement(ShiftsPage),
        handle: { requiredCapabilities: ['scheduling.view'] },
      },
      {
        path: "my-shifts",
        element: React.createElement(MyShiftsPage),
        handle: { requiredCapabilities: ['scheduling.self_service'] },
      },
      {
        path: "procurement",
        element: React.createElement(ProcurementPage),
        handle: { requiredCapabilities: ['procurement.view'] },
      },
      {
        path: "procurement/:id",
        element: React.createElement(ProcurementDetailPage),
      },
      {
        path: "material-requests",
        element: React.createElement(MaterialRequestsPage),
        handle: { requiredCapabilities: ['task_expense.view'] },
      },
      // W3 任务料况 —— task 粒度的物料调度视图，与 material-requests 并列。
      // 同一个 capability：看得到料行的人就该看得到料况。
      {
        path: "task-materials",
        element: React.createElement(TaskMaterialsPage),
        handle: { requiredCapabilities: ['task_expense.view'] },
      },
      {
        path: "task-templates",
        element: React.createElement(TaskTemplatesPage),
        handle: { requiredCapabilities: ['config.task_catalog'] },
      },
      {
        path: "task-templates/new",
        element: React.createElement(TaskTemplateEditorPage),
      },
      {
        path: "task-templates/:id/edit",
        element: React.createElement(TaskTemplateEditorPage),
      },
      {
        path: "task-templates/:id/create-tasks",
        element: React.createElement(TaskTemplateBatchCreatePage),
      },
      {
        path: "workflows/new",
        element: React.createElement(WorkflowEditorPage),
      },
      {
        path: "workflows/:id/edit",
        element: React.createElement(WorkflowEditorPage),
      },
      {
        path: "workflows/runs",
        element: React.createElement(WorkflowRunsPage),
      },
      {
        path: "workflows",
        element: React.createElement(WorkflowsPage),
        handle: { requiredCapabilities: ['config.workflow'] },
      },
      {
        path: "asset-mgr/inventory",
        element: React.createElement(InventoryListPage),
        handle: { requiredCapabilities: ['inventory.view'] },
      },
      {
        path: "asset-mgr/transactions",
        element: React.createElement(InventoryTransactionsPage),
        handle: { requiredCapabilities: ['inventory.view'] },
      },
      // 目录主数据三页（product / category / location）。2026-08-24 后端把
      // config.asset_catalog（tenant_config tier，只有 Account Administrator 配得了）
      // 拆成 business tier 的 asset_catalog.view / .manage / .delete，任意角色可配。
      // 这里挂 .view —— 进页面只要读；写按钮另由 <Can> 门 .manage / .delete。
      {
        path: "asset-mgr/products",
        element: React.createElement(ProductsListPage),
        handle: { requiredCapabilities: ['asset_catalog.view'] },
      },
      {
        path: "asset-mgr/categories",
        element: React.createElement(CategoriesListPage),
        handle: { requiredCapabilities: ['asset_catalog.view'] },
      },
      {
        path: "asset-mgr/locations",
        element: React.createElement(Navigate, { to: "/listings?kind=COMPANY", replace: true }),
      },
      {
        // Ownership buckets（归属主体）— spec 2026-07-05 asset-ownership-redesign.
        // ⚠️ FE route only; the backend still needs the menu seed
        // (db/seeds/sys/menu.json) + role_menu grants for this path, otherwise
        // there is no sidebar entry (handoff §5.1 菜单线登记).
        // 闸门是 assets.view（不是 asset_catalog.*）：该页读的是
        // asset-mgr:ownership:read，后端由 assets.view 认领。此前误挂
        // config.asset_catalog —— 那个 cap 不含 ownership 码，声明与实际 403 面不符。
        path: "asset-mgr/ownerships",
        element: React.createElement(OwnershipsListPage),
        handle: { requiredCapabilities: ['assets.view'] },
      },
      {
        path: "asset-mgr/assets",
        element: React.createElement(AssetsListPage),
        handle: { requiredCapabilities: ['assets.view'] },
      },
      {
        path: "asset-mgr/dashboard",
        element: React.createElement(AssetDashboardPage),
        handle: { requiredCapabilities: ['assets.view'] },
      },
      {
        path: "system/tags",
        element: React.createElement(SystemTagsPage),
        handle: { requiredCapabilities: ['config.custom_fields'] },
      },
      {
        // Plan 5 Slice 4 — admin manage activity_event_type registry
        path: "system/activity-event-types",
        element: React.createElement(SystemActivityEventTypesPage),
        handle: { requiredCapabilities: ['platform.admin'] },  // platform-only(仅 superuser)
      },
      {
        // Plan 5 Slice 5 — OPS eventbus observability dashboard
        path: "ops/eventbus",
        element: React.createElement(OpsEventBusPage),
        handle: { requiredCapabilities: ['platform.admin'] },  // platform-only(仅 superuser)
      },
    ],
  },
  {
    path: "*",
    element: React.createElement(NotFoundPage),
  },
])
